<template>
  <CModal
    :title="'Asignar Reglas: ' + (target.name || '')"
    :color="'dark'"
    size="lg"
    :show.sync="showModal"
  >
    <CForm>     
      <!-- Selector de tipo de regla -->
      <CRow>
        <CCol md="4">
          <CSelect
            label="Tipo de regla"
            :options="fieldOptions"
            :value.sync="form.field"
          />
        </CCol>
        <CCol md="8">
          <CInput
            label="Descripción (opcional)"
            :value.sync="form.description"
          />
        </CCol>
      </CRow>
      <!-- Selector de operador, monto/cantidad y campo extra (producto o cliente) -->
      <CRow>
        <!-- Toda la seccion de productos -->
        <!-- Campo tipo de producto (extra o del concurso) -->
        <CCol md="6" v-if="form.field === 'product_sales' || form.field === 'product_boxes'">
          <CSelect label="Producto" :options="typeProductOptions" :value.sync="form.type_field" />
        </CCol>
        <!-- Campo (producto ) dentro del concurso-->
        <CCol md="6" v-if="form.type_field === 'contest'">
          <CSelect label="Producto" :options="productOptions" :value.sync="form.target" />
        </CCol>
        <!-- Productos adicionales (alta inline) -->
        <CCol md="6" v-if="form.type_field === 'additional'">
          <CInput label="Producto (código)" :value.sync="form.additional_code" />
          <CInput label="Producto (nombre)" :value.sync="form.additional_name" />
          <CButton size="sm" color="dark" @click="pushAdditional()">Añadir</CButton>
          <div class="mt-2">
            <span v-if="!form.additional_products.length" class="text-muted">Sin productos adicionales</span>
            <span v-for="(p, idx) in form.additional_products" :key="p.code" class="badge badge-light border mr-1">
              {{ p.code }} · {{ p.name }}
              <span class="ml-1 text-danger" style="cursor:pointer" @click="removeAdditional(idx)">✕</span>
            </span>
          </div>
          <small class="text-muted d-block mt-1">
            Estos productos aplican <strong>solo</strong> a esta regla. No se agregan al objetivo.
          </small>
        </CCol>

        <!-- Toda la seccion de clientes -->
        <CCol md="4" v-else-if="form.field === 'customers_sales' || form.field === 'customers_boxes'">
          <CSelect label="Cliente" :options="customersOptions" :value.sync="form.target" />
        </CCol>

        <!-- Operador -->
        <CCol :md="getColSize('operator')">
          <CSelect label="Operador" :options="getOperators()" :value.sync="form.operator" />
        </CCol>

        <!-- Monto (si aplica) -->
        <CCol :md="getColSize('amount')" v-if="form.operator !== 'exclude'">
          <CInput :label="isMontoField ? 'Monto' : 'Cantidad'" type="text" :value.sync="form.value" />
        </CCol>
      </CRow>     

      <CRow class="mt-3 mb-0">
        <CCol md="6">
          <CSelect
            label="Asignar a"
            :options="[
              { value: 'solo', label: 'Regla individual' },
              { value: 'grupo', label: 'Grupo de reglas' }
            ]"
            :value.sync="form.assignment"
          />
        </CCol>

        <CCol md="6" v-if="form.assignment === 'grupo'">
          <label class="form-label d-block">Seleccionar grupo</label>
          <div class="d-flex">
            <CSelect
              :options="groupOptions"
              :value.sync="form.group_id"
              class="flex-grow-1"
            />
            <CButton
              size="sm"
              color="secondary"
              class="ml-2 mb-5"
              @click="openGroupRulesModal"
            >
              <CIcon name="cilCheckCircle" /> Nuevo grupo
            </CButton>
          </div>
        </CCol>
      </CRow>


      <!-- Vista previa -->
      <CRow class="mb-4">
        <CCol>
          <p class="mb-0 text-muted"><strong>Vista previa:</strong> {{ formatLiveRule() }}</p>
        </CCol>
      </CRow>

      <!-- Listado de reglas actuales -->
     <CRow class="mb-3">
      <CCol>
        <h6>Reglas asignadas</h6>

        <div v-if="target.rules && target.rules.length">
          <div v-for="(r, i) in target.rules" :key="'solo_' + i" class="d-flex justify-content-between align-items-center border p-2 mb-2 rounded">
            <div>
              <strong>{{ i + 1 }}.</strong> {{ r.preview }}
            </div>
            <CButton color="danger" size="sm" @click="removeRule(r.id, i)">Eliminar</CButton>
          </div>
        </div>

        <div v-if="target.ruleGroups && target.ruleGroups.length">
          <div
            v-for="(group, gi) in target.ruleGroups"
            :key="'grp_' + group.id"
            class="border p-2 mb-3 rounded"
          >
            <div class="mb-2">
              <strong>Grupo:</strong> {{ group.description || 'Grupo sin nombre' }}
            </div>
            <ul class="pl-3 mb-2">
              <li v-for="(r, ri) in group.rules" :key="'grp_' + gi + '_r_' + ri">
                {{ r.preview }}
              </li>
            </ul>
            <CButton color="danger" size="sm" @click="deleteGroup(gi)">Eliminar grupo</CButton>
          </div>
        </div>

        <div v-if="(!Array.isArray(target.rules) || !target.rules.length) 
                && (!Array.isArray(target.ruleGroups) || !target.ruleGroups.length)" 
            class="text-muted">
          Sin reglas aún
        </div>
      </CCol>
    </CRow>

    </CForm>

    <template #footer>
      <CButton @click="showModal = false" color="secondary">Cerrar</CButton>
      <CButton @click="addRule" color="dark">Añadir regla</CButton>
    </template>
    <GroupRulesModal
      :groups="target.ruleGroups"
      @create-group="addGroup"
      @remove-group="deleteGroup"
      ref="groupRulesModal"
    />
  </CModal>
</template>

<script>
import store from '../../../store';
import ws from '../../../services/challenges';
import GroupRulesModal from './GroupRulesModal.vue';

export default {
  name: 'AssignRulesModal',
  components: { GroupRulesModal },
  props: {
  },
  data () {
    return {
      showModal: false,
      form: {
        field: 'sales',
        type_field: '',          // 'contest' | 'additional'
        operator: '>=',
        value: '',
        target: null,            // id producto (concurso) o id cliente
        description: '',
        additional_code: '',     // input inline
        additional_name: '',     // input inline
        additional_products: [],  // [{ code, name }]
        assignment: 'solo',  // 'solo' | 'grupo' (productos adicionales)
        group_id: null
      },
      operatorOptionsBase: [
        { value: '>=', label: 'Mayor o igual que (≥)' },
        { value: '<=', label: 'Menor o igual que (≤)' },
        { value: '==', label: 'Igual a (=)' },
        { value: '>',  label: 'Mayor que (>)' },
        { value: '<',  label: 'Menor que (<)' }
      ],
      productOptions: [],
      typeProductOptions: [
        { value: "contest", label: 'Concurso' },
        { value: "additional", label: 'Adicional' },
      ],
      customersOptions: [],
      target: {
        id: null,
        name: '',
        rules: [],
        type: ''
      },
    }
  },
  computed: {
    groupOptions () {
      return this.target.ruleGroups.map(g => ({
        value: g.id,
        label: g.description || 'Grupo sin nombre'
      }))
    },
    isMontoField () {
      return ['sales', 'producto', 'customers_sales','product_sales'].includes(this.form.field)
    },
    fieldOptions () {
      return [
        { value: 'sales', label: 'Ventas Monto (Total)' },
        { value: 'boxes', label: 'Cajas Unidad (Cantidad)' },
        { value: 'customers', label: 'Clientes Cantidad (Diferentes)' },
        { value: 'customers_sales', label: 'Venta por Cliente (Total)' },
        { value: 'customers_boxes', label: 'Cajas por Cliente (Cantidad)' },
        { value: 'product_sales', label: 'Venta por Producto (Total)' },
        { value: 'product_boxes', label: 'Cajas por Producto (Cantidad)' }
      ]
    }
  },
  mounted: async function() {
    // Obtenemos los productos del concurso
    const response = await ws.getProducts(this.$route.params.challenge_id);

    if(response.type === 'success'){
      this.productOptions = response.data.map(p => ({
        value: p.product_id,
        label: `${p.product_code} - ${p.product_name}`
      }));
    } else {
      this.showToast('danger', response.message || 'Error al obtener productos');
    }

    //Obtenemos los clientes del concurso
    const clientResponse = await ws.getCustomers(this.$route.params.challenge_id);
    if(clientResponse.type === 'success'){
      this.customersOptions = clientResponse.data.map(c => ({
        value: c.id,
        label: `${c.code} - ${c.name}`
      }));
    } else {
      this.showToast('danger', clientResponse.message || 'Error al obtener clientes');
    }
  },
  methods: {
    openGroupRulesModal (item) {
      this.$refs.groupRulesModal.openModal(item);
    },
    async addGroup (group) {
      this.loading();
      const response = await ws.storeRuleGroup({
        challenge_id: this.target.challenge_id,
        challenge_objective_rule_id: this.target.id,
        description: group.description || 'Grupo sin nombre',
        operator: 'OR'
      });

      if (response.type === 'success') {
        // aquí empujas el nuevo grupo normalizado
        const newGroup = {
          ...response.data,
          rules: Array.isArray(response.data.rules) ? response.data.rules : [] // garantizas array
        }
        this.target.ruleGroups.push(newGroup); // añade al array
        this.form.group_id = newGroup.id; // selecciona el nuevo grupo
        this.showToast('success', 'Grupo creado');
      } else {
        this.showToast('danger', response.message || 'Error al crear grupo');
      }
      this.loaded();
    },
    async deleteGroup (index) {
      const group = this.target.ruleGroups[index];
      this.loading();
      const response = await ws.deleteRuleGroup(group.id);

      if (response.type === 'success') {
        this.target.ruleGroups.splice(index, 1);
        this.showToast('success', 'Grupo eliminado');
      } else {
        this.showToast('danger', response.message || 'Error al eliminar grupo');
      }
      this.loaded();
    },
    pushAdditional () {
      const code = (this.form.additional_code || '').trim()
      const name = (this.form.additional_name || '').trim()
      if (!code || !name) return this.showToast('danger', 'Código y nombre son requeridos')

      const exists = this.form.additional_products.some(p => p.code === code)
      if (exists) return this.showToast('danger', 'Ese código ya fue añadido')

      this.form.additional_products.push({ code, name })
      this.form.additional_code = ''
      this.form.additional_name = ''
    },
    removeAdditional (idx) {
      this.form.additional_products.splice(idx, 1)
    },
    getColSize(part) {
      if (part === 'operator') {
        if (this.form.field === 'sales' || this.form.field === 'boxes' || this.form.field === 'customers') {
          return 6
        }
        if(this.form.field === 'customers_sales' || this.form.field === 'customers_boxes'){
          return 4
        }
        return 6
      }
      if (part === 'amount') {
        if (this.form.field === 'sales' || this.form.field === 'boxes' || this.form.field === 'customers') {
          return 6
        }
        if(this.form.field === 'customers_sales' || this.form.field === 'customers_boxes'){
          return 4
        }
        return 6
      }
      return 4
    },
    getFirstAvailableField () {
      const options = this.fieldOptions
      return Array.isArray(options) && options.length ? options[0].value : ''
    },
    openModal (item) {
      this.target = item;
      const defaultField = this.getFirstAvailableField()
      this.form = {
        field: defaultField,
        type_field: defaultField.startsWith('product_') ? 'contest' : '',
        operator: '>=',
        value: '',
        target: null,
        description: '',
        additional_code: '',
        additional_name: '',
        additional_products: [],
        assignment: 'solo',
        group_id: null
      }

      // Si es tipo producto o cliente VIP, asignar primer elemento
      if ((defaultField === 'product_sales' || defaultField === 'product_boxes') && this.productOptions.length > 0) {
        this.form.target = this.productOptions[0].value
      } else if ((defaultField === 'customers_sales' || defaultField === 'customers_boxes') && this.customersOptions.length > 0) {
        this.form.target = this.customersOptions[0].value
      }

      if (!Array.isArray(item.rules)) {
        this.$set(item, 'rules', [])
      }
      if (!Array.isArray(item.ruleGroups)) {
        this.$set(item, 'ruleGroups', []); // 👈 se asegura que esté inicializado
      }
      this.showModal = true
      
    },
    async addRule () {
      this.loading()

      if (!this.form.field || (this.form.operator !== 'exclude' && this.form.value === '')) {
        this.showToast('danger', 'Faltan datos');
        this.loaded()
        return
      }

      const rule = {
        ...this.form,
        preview: this.formatLiveRule(this.form),
        challenge_rule_id: this.target.id,
        challenge_id: this.target.challenge_id,
        product_id: (this.form.type_field === 'contest') ? this.form.target : null,
        additional_products: (this.form.type_field === 'additional') ? this.form.additional_products : [],
        group_id: this.form.assignment === 'grupo' ? this.form.group_id : null
      }

      const response = await ws.storeRules(rule)

      if (response.type !== 'success') {
        this.showToast('danger', response.message || 'Error al guardar la regla')
        this.loaded();
        return
      }

      // Asignar según destino
      if (this.form.assignment === 'grupo') {
        const group = this.target.ruleGroups.find(g => g.id === this.form.group_id)
        if (group) {
          group.rules.push(response.data)
        } else {
          this.showToast('danger', 'Grupo no encontrado')
        }
      } else {
        this.target.rules.push(response.data)
      }

      // Reset form
      this.form = {
        field: 'sales',
        type_field: '',
        operator: '>=',
        value: '',
        target: null,
        description: '',
        additional_code: '',
        additional_name: '',
        additional_products: [],
        assignment: 'solo',
        group_id: null
      }

      this.loaded()
      this.$emit('toast', { type: 'success', message: 'Regla añadida' })
    },
    async removeRule(id,index) {
      this.loading();
      const response = await ws.deleteRules(id);
      if(response.type != "success"){
        this.showToast('danger', response.message || 'Error al guardar la regla');
        return;
      }
      this.target.rules.splice(index, 1)      
      this.loaded();
    },
    formatLiveRule (rule = null) {
      const f = rule || this.form
      if (!f.field) return ''

      const field = this.fieldOptions.find(opt => opt.value === f.field)?.label || f.field
      const operator = this.getOperators().find(opt => opt.value === f.operator)?.label || f.operator
      const value = f.value
      const isExclude = f.operator === 'exclude'
      const label = f.label

      const getLabel = (type, val, fallback) => {
        if (type === 'product_sales' || type === 'product_boxes') {
          return this.productOptions.find(p => p.value === val)?.label || fallback
        }
        if (type === 'customers_sales' || type === 'customers_boxes') {
          return this.customersOptions.find(c => c.value === val)?.label || fallback
        }
        return fallback
      }

      const targetLabel = (() => {
        if (['product_sales', 'product_boxes'].includes(f.field)) {
          if (f.type_field === 'contest') {
            return getLabel(f.field, f.target, '')
          }
          if (f.type_field === 'additional' && Array.isArray(f.additional_products) && f.additional_products.length) {
            return f.additional_products.map(p => `${p.code} - ${p.name}`).join(', ')
          }
        }

        if (['customers_sales', 'customers_boxes'].includes(f.field)) {
          return getLabel(f.field, f.target, '')
        }

        return ''
      })()


      if (isExclude) {
        return `Excluir ${targetLabel || field}`
      }

      switch (f.field) {
        case 'sales':
          return `Debe vender ${operator} ${value} en total`
        case 'customers':
          return `Debe atender ${operator} ${value} clientes`
        case 'boxes':
          return `Debe vender ${operator} ${value} cajas`
        case 'product_sales':
          return `Debe vender ${operator} ${value} en monto de producto ${targetLabel}`
        case 'product_boxes':
          return `Debe vender ${operator} ${value} en cajas de producto ${targetLabel}`
        case 'customers_sales':
          return `Debe vender ${operator} ${value} en monto al cliente ${targetLabel}`
        case 'customers_boxes':
          return `Debe vender ${operator} ${value} en cajas al cliente ${targetLabel}`
        default:
          return `${field} ${operator} ${value}`
      }
    },
    formatRule (r) {
      return this.formatLiveRule(r)
    },
    getOperators () {
      const allowExclude = ['product_sales','product_boxes', 'customers_sales','customers_boxes'].includes(this.form.field)
      const ops = [...this.operatorOptionsBase]
      if (allowExclude) {
        // ops.push({ value: 'exclude', label: 'Excluir' })
      }
      return ops
    },
    showToast(type, message){
      store.commit('toast', {color: type, message: message});
    },
    loading(){
      store.commit('loading');
    },
    loaded(){
      store.commit('loaded');
    },
  },
  watch: {
    'form.field'(newField) {
      const isProductField = ['product_sales', 'product_boxes'].includes(newField)
      const isClientField = ['customers_sales', 'customers_boxes'].includes(newField)

      if (isProductField) {
        this.form.type_field = 'contest'
        if (!this.form.target && this.productOptions.length > 0) {
          this.form.target = this.productOptions[0].value
        }
      } else {
        this.form.type_field = ''
        this.form.target = isClientField && this.customersOptions.length > 0
          ? this.customersOptions[0].value
          : null
        this.form.additional_products = []
        this.form.additional_code = ''
        this.form.additional_name = ''
      }
    },
    'form.type_field'(val) {
      if (val === 'contest') {
        if (!this.form.target && this.productOptions.length > 0) {
          this.form.target = this.productOptions[0].value
        }
        this.form.additional_products = []
        this.form.additional_code = ''
        this.form.additional_name = ''
      } else if (val === 'additional') {
        this.form.target = null
      }
    },
    'form.assignment' (val) {
      if (val === 'grupo' && this.target.ruleGroups.length === 1) {
        this.form.group_id = this.target.ruleGroups[0].id;
      }
    }
  }
}
</script>


